import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  DialogTitle,
  Divider,
  InputAdornment,
  TextField,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  CalendarIcon,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import {
  Dialog,
  CustomDropdownField,
  EsgSingleSelectControlledField,
  Label14Medium,
  InfoTooltip,
} from "components";
import { EsgMultiselectControlledField } from "components/inputs/esg-multi-select-field";
import { useNotify } from "hooks";
import { getDateFormatset } from "utils";
import {
  useInitiativeTypes,
  useInitiativeCategories,
  useInitiativeKpiOptions,
  useCreateInitiative,
} from "hooks/api/initiatives";
import { useGetUsers } from "hooks/api/users";
import { useGetAllowedValues } from "hooks/api/allowed-values";
import { useAuthenticatedUserState } from "recoils/user";
import { CreateInitiativeDTO } from "api/initiatives/types";

import {
  CreateInitiativeFormState,
  CreateInitiativeModalProps,
  DEFAULT_FORM_STATE,
} from "./types";
import { LEVEL_OPTIONS, PRIORITY_COLORS } from "./consts";
import { inputBaseSx, numericInputSx, multiSelectOwnerSx } from "./styles";
import { SectionLabel, FieldLabel, LevelEntityField } from "./components";

export const CreateInitiativeModal: React.FC<CreateInitiativeModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateInitiativeFormState, string>>
  >({});
  const [isSaving, setIsSaving] = useState(false);

  const notify = useNotify();
  const currentUser = useAuthenticatedUserState();

  const defaultFormState = useMemo(
    () => ({ ...DEFAULT_FORM_STATE, ownerIds: [String(currentUser.id)] }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [form, setForm] = useState<CreateInitiativeFormState>(defaultFormState);

  const { data: initiativeTypes = [], isLoading: loadingTypes } =
    useInitiativeTypes();
  const { data: categories = [], isLoading: loadingCategories } =
    useInitiativeCategories();
  const { data: kpiOptions = [], isLoading: loadingKpi } =
    useInitiativeKpiOptions();
  const { data: users = [], isLoading: loadingOwners } = useGetUsers();
  const { data: allowedValues } = useGetAllowedValues();
  const createInitiativeMutation = useCreateInitiative();

  const updateField = useCallback(
    <K extends keyof CreateInitiativeFormState>(
      field: K,
      value: CreateInitiativeFormState[K],
    ) => {
      setForm((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    [],
  );

  const handleClose = useCallback(() => {
    setForm(defaultFormState);
    setErrors({});
    onClose();
  }, [onClose, defaultFormState]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof CreateInitiativeFormState, string>> =
      {};
    if (!form.name.trim()) newErrors.name = "Initiative name is required";
    if (!form.category) newErrors.category = "Category is required";
    if (!form.type) newErrors.type = "Initiative type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const payload: CreateInitiativeDTO = {
        name: form.name.trim(),
        level: form.level,
        level_entity:
          form.level !== "GLOBAL" && form.levelEntity ? form.levelEntity : null,
        category: form.category,
        type: form.type,
        kpi: form.kpi || null,
        start_date: form.startDate
          ? dayjs(form.startDate).format("YYYY-MM-DD")
          : null,
        end_date: form.endDate
          ? dayjs(form.endDate).format("YYYY-MM-DD")
          : null,
        priority: form.priority,
        owner: form.ownerIds.map(Number),
        capex: form.capex ? Number(form.capex) : null,
        annual_gross_savings: form.annualGrossSavings
          ? Number(form.annualGrossSavings)
          : null,
        annualTco2eAvoided: form.annualTco2eAvoided
          ? Number(form.annualTco2eAvoided)
          : null,
      };
      await createInitiativeMutation.mutateAsync(payload);
      notify.success("Initiative created successfully!");
      handleClose();
      onSuccess?.();
    } catch {
      notify.error("Failed to create initiative. Please try again.");
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form,
    currentUser,
    createInitiativeMutation,
    notify,
    handleClose,
    onSuccess,
  ]);

  // Options derived from API responses — values are names (what the API accepts)
  const typeOptions = initiativeTypes.map((t) => ({
    label: t.typeName,
    value: t.typeName,
  }));
  const categoryOptions = categories.map((c) => ({
    label: c.categoryName,
    value: c.categoryName,
  }));
  const kpiSelectOptions = kpiOptions.map((k) => ({
    label: k.kpiName,
    value: k.kpiName,
  }));

  const ownerOptions = users.map((u) => {
    const name = [u.firstName, u.lastName].filter(Boolean).join(" ") || u.email;
    return { label: name, value: String(u.id), name };
  });
  const selectedOwners = ownerOptions.filter((o) =>
    form.ownerIds.includes(o.value),
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      actions={
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            width: "100%",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSaving}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving}
            sx={{ borderRadius: 2 }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </Box>
      }
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 0,
          pt: 0,
          pb: "10px",
        }}
      >
        Create New Initiative
      </DialogTitle>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Label14Medium color="text.secondary" sx={{ mb: 2.5 }}>
          Start with the basics. You can configure financials and impact next.
        </Label14Medium>
        <Divider sx={{ mb: 2.5 }} />

        {/* ── Basic Info ─────────────────────────────────────────── */}
        <Box sx={{ mb: 2 }}>
          <FieldLabel required>Initiative Name</FieldLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Example: Solar Panel Installation – Phase 1"
            value={form.name}
            onChange={(e) => updateField("name", e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            sx={numericInputSx}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <CustomDropdownField
            label="Category"
            options={categoryOptions}
            selectedValue={form.category}
            placeholder={
              loadingCategories ? "Loading..." : "Select category..."
            }
            onChange={(v) => updateField("category", v)}
            showCustomOption={false}
            required
            error={!!errors.category}
            errorMessage={errors.category}
            sx={{ width: "100%" }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <CustomDropdownField
            label="Initiative Type"
            options={typeOptions}
            selectedValue={form.type}
            placeholder={loadingTypes ? "Loading..." : "Select type..."}
            onChange={(v) => updateField("type", v)}
            required
            error={!!errors.type}
            errorMessage={errors.type}
            sx={{ width: "100%" }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <EsgSingleSelectControlledField
              label="Level"
              options={LEVEL_OPTIONS}
              selectedValue={form.level}
              setSelectedValue={(v) => {
                updateField("level", v as CreateInitiativeFormState["level"]);
                updateField("levelEntity", "");
              }}
              placeholder="Select level..."
              width="100%"
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <LevelEntityField
              level={form.level}
              value={form.levelEntity}
              onChange={(v) => updateField("levelEntity", v)}
              allowedValues={allowedValues}
              error={errors.levelEntity}
            />
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <CustomDropdownField
            label="Primary KPI Impacted"
            options={kpiSelectOptions}
            selectedValue={form.kpi}
            placeholder={loadingKpi ? "Loading..." : "Select primary impact..."}
            onChange={(v) => updateField("kpi", v)}
            error={!!errors.kpi}
            errorMessage={errors.kpi}
            sx={{ width: "100%" }}
          />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Timeline ───────────────────────────────────────────── */}
        <SectionLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarIcon /> Timeline
          </Box>
        </SectionLabel>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>Start Date</FieldLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format={getDateFormatset("/")}
                value={form.startDate ? dayjs(form.startDate) : null}
                onChange={(d: Dayjs | null) =>
                  updateField("startDate", d ? d.toISOString() : "")
                }
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: !!errors.startDate,
                    helperText: errors.startDate,
                    sx: numericInputSx,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>End Date</FieldLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format={getDateFormatset("/")}
                value={form.endDate ? dayjs(form.endDate) : null}
                onChange={(d: Dayjs | null) =>
                  updateField("endDate", d ? d.toISOString() : "")
                }
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: numericInputSx,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Ownership ──────────────────────────────────────────── */}
        <SectionLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            Ownership
          </Box>
        </SectionLabel>

        <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
          <Box sx={{ flex: 1, ...multiSelectOwnerSx }}>
            <EsgMultiselectControlledField
              label="Owner"
              options={ownerOptions}
              selectedValues={selectedOwners}
              setSelectedValues={(
                selected: Array<{ value: string | number }>,
              ) =>
                updateField(
                  "ownerIds",
                  selected.map((o) => String(o.value)),
                )
              }
              disabled={loadingOwners}
              allowSearchbar
            />
            {errors.ownerIds && (
              <Box sx={{ fontSize: "0.75rem", color: "error.main", mt: 0.5 }}>
                {errors.ownerIds}
              </Box>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>Priority</FieldLabel>
            <Box sx={{ display: "flex", gap: 1 }}>
              {(["LOW", "MEDIUM", "HIGH"] as const).map((p) => {
                const selected = form.priority === p;
                const colors = PRIORITY_COLORS[p];
                return (
                  <Box
                    key={p}
                    onClick={() => updateField("priority", p)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      px: 1.5,
                      height: "36px",
                      borderRadius: "30px",
                      border: `1px solid ${
                        selected ? colors.border : "#D0D5DD"
                      }`,
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: 600,
                      fontFamily: "Inter",
                      color: selected ? colors.text : "#8A94A6",
                      backgroundColor: selected ? colors.bg : "transparent",
                      transition:
                        "border-color 0.15s, color 0.15s, background-color 0.15s",
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: colors.dot,
                        flexShrink: 0,
                      }}
                    />
                    {p.charAt(0) + p.slice(1).toLowerCase()}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* ── Financial Estimate ─────────────────────────────────── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 0.75,
            mb: 1.5,
            color: "inherit",
          }}
        >
          <SectionLabel>Add initial financial estimate</SectionLabel>
          <InfoTooltip
            width="16px"
            height="16px"
            tooltip="This allows summary metrics to pre-populate later. Detailed modeling available after creation."
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>Estimated CAPEX</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="0"
              value={form.capex}
              onChange={(e) => updateField("capex", e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">USD</InputAdornment>
                ),
              }}
              sx={inputBaseSx}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>Estimated Annual Savings</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="0"
              value={form.annualGrossSavings}
              onChange={(e) =>
                updateField("annualGrossSavings", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">$</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">USD/yr</InputAdornment>
                ),
              }}
              sx={inputBaseSx}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FieldLabel>Estimated Annual CO2e Avoided</FieldLabel>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="0"
              value={form.annualTco2eAvoided}
              onChange={(e) =>
                updateField("annualTco2eAvoided", e.target.value)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">tCO2e/yr</InputAdornment>
                ),
              }}
              sx={inputBaseSx}
            />
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
